import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

type Props = {
  disabled: boolean;
  onSend: (text: string) => void;
};

// Minimal typings for the (still vendor-prefixed) Web Speech API.
type SpeechRecognitionResult = {
  isFinal: boolean;
  0: { transcript: string };
};
type SpeechRecognitionEvent = {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResult };
};
type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export default function Composer({ disabled, onSend }: Props) {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const recogRef = useRef<SpeechRecognitionInstance | null>(null);
  const baseTextRef = useRef<string>("");
  const [speechSupported] = useState<boolean>(() => getSpeechRecognitionCtor() !== null);

  // Auto-resize.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [value]);

  // Stop recognition on unmount.
  useEffect(() => {
    return () => {
      try { recogRef.current?.stop(); } catch { /* noop */ }
    };
  }, []);

  const submit = useCallback(() => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
  }, [value, disabled, onSend]);

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const stopListening = useCallback(() => {
    try { recogRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const recog = new Ctor();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    baseTextRef.current = value.length > 0 && !value.endsWith(" ") ? value + " " : value;
    recog.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r[0].transcript;
        if (r.isFinal) final += t;
        else interim += t;
      }
      setValue(baseTextRef.current + final + interim);
      if (final) baseTextRef.current += final;
    };
    recog.onerror = () => {
      setListening(false);
    };
    recog.onend = () => {
      setListening(false);
      recogRef.current = null;
    };
    recogRef.current = recog;
    try {
      recog.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [value]);

  const toggleMic = () => {
    if (disabled) return;
    if (listening) stopListening();
    else startListening();
  };

  return (
    <div className="composer">
      <textarea
        ref={taRef}
        className="composer-input"
        placeholder={disabled ? "Streaming..." : "Type a message (Enter to send, Shift+Enter for newline)"}
        rows={1}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKey}
      />
      {speechSupported && (
        <button
          type="button"
          className={`composer-mic${listening ? " composer-mic-active" : ""}`}
          disabled={disabled}
          onClick={toggleMic}
          aria-label={listening ? "Stop voice input" : "Start voice input"}
          title={listening ? "Stop voice input" : "Start voice input"}
        >
          {listening ? (
            <>
              <span className="composer-mic-dot" aria-hidden="true" />
              Stop
            </>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          )}
        </button>
      )}
      <button
        type="button"
        className="composer-send"
        disabled={disabled || !value.trim()}
        onClick={submit}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <path d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2z" />
        </svg>
        Send
      </button>
    </div>
  );
}
